import { ClipboardCheck, Database, ShieldAlert } from 'lucide-react';
import { Badge, Card, DataTable, EmptyState } from '@/components/ui';
import { useIntegrations } from '@/features/admin-v2/api';
import { PageContainer } from '@/layouts/PageContainer';
import { OperationalRecords } from './OperationalRecords';
import { Branches, Permissions, Rosters, Staff } from './SetupPages';
import { DiscountSchemePage } from '@/pages/setup/DiscountSchemePage';
import { ReportsPage } from './ReportsPage';
import { BooksPage } from './BooksPage';
import { integrationStatus } from './integration-status';
import styles from './OperationalRecords.module.css';
import { dayLabel } from './register-format';

export type OpsMode =
  | 'branches'
  | 'rosters'
  | 'discounts'
  | 'compliance'
  | 'dataSecurity'
  | 'staff'
  | 'permissions'
  | 'integrations'
  | 'groupReports'
  | 'goLive'
  | 'books'
  | 'reports';

export function AdminOperationsPage({ mode }: { mode: OpsMode }) {
  if (mode === 'branches') return <Branches />;
  if (mode === 'staff') return <Staff />;
  if (mode === 'permissions') return <Permissions />;
  if (mode === 'integrations') return <Integrations />;
  if (mode === 'compliance') return <Compliance />;
  if (mode === 'dataSecurity') return <DataSecurity />;
  if (mode === 'goLive') return <GoLive />;
  if (mode === 'groupReports' || mode === 'reports')
    return <Reports group={mode === 'groupReports'} />;
  if (mode === 'books') return <BooksPage />;
  if (mode === 'discounts') return <DiscountSchemePage />;
  return <Rosters />;
}

function Compliance() {
  return (
    <OperationalRecords
      title="Compliance"
      kinds={[
        'licence',
        'equipment',
        'waste_collection',
        'incident',
        'policy',
        'advertising',
      ]}
      icon={<ClipboardCheck size={22} />}
    />
  );
}
function DataSecurity() {
  return (
    <OperationalRecords
      title="Data & security"
      kinds={[
        // RL-1913: patients' "Who opened my record" questions (answered here).
        'record_access_query',
        'breach_response',
        'retention_rule',
        'backup',
        'security_finding',
        'monitoring_alert',
        'signing_certificate',
        'ai_error',
      ]}
      icon={<ShieldAlert size={22} />}
    />
  );
}
function GoLive() {
  return (
    <OperationalRecords
      title="Go-live"
      kinds={['migration_item', 'cutover_step', 'support_ticket', 'hardware']}
      icon={<Database size={22} />}
    />
  );
}
function Integrations() {
  const integrations = useIntegrations();
  return (
    <PageContainer>
      <Card
        title="Integrations"
        flush
        actions={<Badge tone="info">Unavailable providers simulated</Badge>}
      >
        <DataTable
          caption="Integrations"
          rows={integrations.data}
          rowKey={(i) => i.id ?? i.name}
          loading={integrations.isLoading}
          error={integrations.error}
          onRetry={() => void integrations.refetch()}
          empty={<EmptyState title="No integrations configured" />}
          columns={[
            {
              key: 'name',
              header: 'Service',
              render: (i) => <strong>{i.name}</strong>,
            },
            {
              key: 'provider',
              header: 'Provider',
              render: (i) => i.provider ?? '-',
            },
            {
              key: 'status',
              header: 'Status',
              // RL-1824 (gap #118): plain words, not `not_configured`.
              render: (i) => {
                const st = integrationStatus(i.status);
                return <Badge tone={st.tone}>{st.label}</Badge>;
              },
            },
            {
              key: 'event',
              header: 'Last event',
              render: (i) => i.lastEvent ?? '-',
            },
            {
              key: 'key',
              header: 'Key last rotated',
              render: (i) =>
                i.keyRotatedAt ? (
                  <>
                    {dayLabel(i.keyRotatedAt)}
                    {i.keyFingerprint && (
                      <span className={`${styles.sub} mono`}>
                        {i.keyFingerprint}
                      </span>
                    )}
                  </>
                ) : (
                  'Not rotated yet'
                ),
            },
            {
              key: 'mode',
              header: 'Mode',
              render: (i) =>
                i.simulated ? (
                  <Badge tone="info">Simulated</Badge>
                ) : (
                  <Badge tone="success">Live</Badge>
                ),
            },
          ]}
        />
      </Card>
      {/* RL-1824 (decision D31): no rotate button here. */}
      <p className={styles.sub}>
        Keys are rotated by the system team. Ask them if a key needs changing.
      </p>
    </PageContainer>
  );
}

function Reports({ group }: { group: boolean }) {
  return <ReportsPage group={group} />;
}
